import { Outlet } from 'react-router';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { currentReturnTo } from '@/auth/return-to';
import { TopBar } from './topbar/topbar';
import { Sidebar } from './sidebar/sidebar';
import { SidebarRail } from './sidebar/sidebar-rail';
import { ReconnectionBanner } from './reconnection-banner';
import { ExerciseBanner } from './exercise-banner';
import { TenantGuard } from '@/auth/tenant-guard';
import { NexaBootSplash } from './nexa-mark/nexa-boot-splash';
import { NexaQueryActivity } from './nexa-mark/nexa-query-activity';
import { useFocusMode, useImmersiveMode } from './focus-mode';
import { AlertsHost } from './alerts/alerts-host';
import { AlertsLayer } from './alerts/alerts-layer';
import { OsNotifyBridge } from '@/lib/os-notify/os-notify.bridge';
import { InstallPrompt } from './fullscreen/install-prompt';
import { useKeepFullscreen } from './fullscreen/fullscreen';

// App shell composes top bar + sidebar + breadcrumb + main per docs/ui/operator-ui-tds.md §4.3.
// Wrapped in withAuthenticationRequired so children always have a populated session —
// mirrors apps/admin-ui/src/routes/root-layout.tsx pattern.
//
// TenantGuard then enforces the pinned-org contract per §3.2: a token whose airline
// claim disagrees with VITE_TENANT lands the user on /forbidden, never on a data view.

function BareAppShell() {
  // «Modo foco» while declaring: the sidebar folds into its icon rail (`focus-mode.ts`).
  const focus = useFocusMode();
  // «Operar la disrupción» goes further: no top bar, and the sidebar folds into its icon
  // rail on a desktop (none on a phone or tablet) — the screen draws its own full-screen
  // frame beside it and closes with its own ✕ (`focus-mode.ts`).
  const immersive = useImmersiveMode();
  // On an iPad the keyboard takes the page out of full screen: back at the next tap.
  useKeepFullscreen();
  return (
    <TenantGuard>
      {/* App-shell (frontend-conventions §18.1): fixed 100dvh frame, the document
          never scrolls. `dvh` (not `vh`) so mobile browser chrome is handled. The
          single scroll region is <main> (or, for the case workspace, a region the
          route owns); chrome (top bar, sidebar) stays put. The breadcrumb now
          lives INSIDE the top bar (NEX-299 item 1) — no standalone strip. min-h-0
          on every flex child so the inner scroller can actually shrink — and min-w-0
          on <main> for the same reason on the other axis, which is NOT symmetric
          decoration: a flex item defaults to `min-width: auto`, so it refuses to
          shrink below its content's intrinsic width. Any page holding a wide element
          in its own `overflow-x-auto` scroller (a 40-column import grid) would push
          <main> wider than the viewport instead of scrolling inside it, and the
          `overflow-y-auto` here only catches the vertical axis. It stayed invisible
          for as long as every page was a centred `max-w-*` column.

          `relative` on the frame AND on every scroll region (here <main>; the sidebar's
          list in `sidebar.tsx`) is NOT decoration either. An absolutely positioned element
          — every `sr-only` text, Radix's hidden native inputs — is placed against its
          nearest POSITIONED ancestor and clipped only by the overflow of that ancestor or
          one inside it. With none positioned, a `sr-only` cell far down a long table
          (Proveedores) was placed against the viewport, escaped every clip and made the
          DOCUMENT taller: the page grew an outer scroll, and focusing that element scrolled
          the top bar out of sight. `overflow: clip` on the frame (where supported) also
          refuses programmatic scrolling of the frame itself — `hidden` still allows it. */}
      <div className="relative flex h-[100dvh] flex-col overflow-hidden supports-[overflow:clip]:overflow-clip">
        {/* #812: the rehearsal strip, above the chrome. Nothing renders off the mode.
            It stays above <main> in immersive mode too: «Operar la disrupción» fills
            <main> (absolute), never the viewport, so the strip is never covered. */}
        <ExerciseBanner />
        {immersive ? null : <TopBar />}
        {/* `relative`: the operator-alert dialogs (alerts-layer.tsx) stand over THIS frame —
            sidebar + content, under the top bar and the rehearsal strip, never portalled
            over them (operator-notifications brief §6 inv. 8). */}
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          {/* Full screen (Operar) and declaring (/express): the menu folded to icons,
              from `lg` up only — the rail hides itself below that. A sibling BEFORE
              <main>, so the screen's frame (absolute over <main>) stands to its right,
              never over it. */}
          {immersive || focus ? <SidebarRail /> : <Sidebar />}
          {/* Main content spans the full remaining width (NEX-299 item 3) — no
              centered max-w container here; pages own their own padding. */}
          <main className="relative min-h-0 min-w-0 flex-1 overflow-y-auto">
            <Outlet />
          </main>
          {/* «Operar» draws its own layer under its header, like WorkSwitch. */}
          {immersive ? null : <AlertsLayer />}
        </div>
        {/* «Operar la disrupción» draws the pill under its own header instead: here,
            with no top bar any more, it would land on the screen's state badge. */}
        {immersive ? null : <ReconnectionBanner />}
        {/* Operator alerts: presence, focus, delivery policy, catch-up, live region. */}
        <AlertsHost />
        {/* …and, while the tab is hidden, the OS notification the operator opted into. */}
        <OsNotifyBridge />
        {/* On a tablet, after entering (once a week at most): «Usa Nexa como app» —
            how to add it to the home screen (install-prompt.tsx). */}
        <InstallPrompt />
      </div>
      <NexaQueryActivity />
    </TenantGuard>
  );
}

export const AppShell = withAuthenticationRequired(BareAppShell, {
  onRedirecting: () => <NexaBootSplash />,
  // The SDK's default returnTo keeps only `pathname`. Half this console's
  // screens are shared as a path PLUS a query (`?view=attention`,
  // `?airports=SCL,LIM`), and dropping it fixes the route while losing the
  // view. `callback.tsx` consumes this via `takeReturnTo()`.
  returnTo: currentReturnTo,
});
